import {
  Children,
  cloneElement,
  createContext,
  FC,
  isValidElement,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useOnClickOutside } from "src/hooks/useOnClickOutside";
import { createPortal } from "react-dom";
import { Dropdown as SemanticUIDropdown } from "semantic-ui-react";
import { omit } from "lodash-es";
import { Error } from "src/atoms/Input/Input.styled";
import { ArrowDown } from "src/icons";
import { useScrollableRowRef } from "src/atoms/ScrollableRow/ScrollableRow";
import {
  StyledDropdown,
  StyledDropdownInline,
  FilterWrapper,
  FilterButton,
  FilterChevron,
  FilterDropdownPanel,
} from "./Dropdown.styled";

const DropdownWrapperRefContext = createContext<
  RefObject<HTMLDivElement> | undefined
>(undefined);
export const useDropdownWrapperRef = () =>
  useContext(DropdownWrapperRefContext);

const SemanticUIDropdownWrapper = (props: any) => (
  <>
    <SemanticUIDropdown
      selection
      selectOnBlur={false}
      selectOnNavigation={false}
      {...omit(props, ["errorMessage"])}
    />
    {props?.errorMessage ? (
      <Error data-testid="dropdown-error">{props.errorMessage}</Error>
    ) : null}
  </>
);

const Dropdown = StyledDropdown.withComponent(SemanticUIDropdownWrapper);
const DropdownInline = StyledDropdownInline.withComponent(
  SemanticUIDropdownWrapper
);

const DROPDOWN_WIDTH = 288;
const VIEWPORT_PADDING = 16;

interface DropdownButtonProps {
  opened?: boolean;
  disabled?: boolean;
  hasActiveFilters?: boolean;
  onClick?: () => void;
  children?: ReactNode;
}

const DropdownButton: FC<DropdownButtonProps> = ({
  children,
  opened,
  disabled,
  hasActiveFilters,
  onClick,
}) => (
  <FilterButton
    $disabled={disabled}
    $active={hasActiveFilters}
    onClick={disabled ? undefined : onClick}
  >
    {children}
    <FilterChevron $opened={opened}>
      <ArrowDown width="12px" height="6px" color="#000" />
    </FilterChevron>
  </FilterButton>
);

interface DropdownItemsProps {
  opened?: boolean;
  wrapperRef?: RefObject<HTMLDivElement>;
  children?: ReactNode;
  width?: number;
}

const DropdownItems: FC<DropdownItemsProps> = ({
  children,
  opened,
  wrapperRef,
  width,
}) => {
  const containerRef = useScrollableRowRef();
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useEffect(() => {
    if (!opened || !wrapperRef?.current) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;

      const top = rect.bottom + 8;
      let left = rect.left;

      const containerRect = containerRef?.current?.getBoundingClientRect();
      const rightEdge =
        containerRect?.right ?? window.innerWidth - VIEWPORT_PADDING;
      const leftEdge = containerRect?.left ?? VIEWPORT_PADDING;

      if (left + DROPDOWN_WIDTH > rightEdge) {
        left = rect.right - DROPDOWN_WIDTH;
      }
      left = Math.max(left, leftEdge);
      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [opened, wrapperRef, containerRef]);

  if (!opened || !position) return null;

  return createPortal(
    <DropdownWrapperRefContext.Provider value={wrapperRef}>
      <FilterDropdownPanel
        $top={position.top}
        $left={position.left}
        $width={width}
        data-dropdown-panel
      >
        {children}
      </FilterDropdownPanel>
    </DropdownWrapperRefContext.Provider>,
    document.body
  );
};

type Clickable = {
  onClick?: () => void;
};

interface DropdownOutlinedProps {
  opened?: boolean;
  disabled?: boolean;
  hasActiveFilters?: boolean;
  children?: ReactNode;
  width?: number;
  onClose?: () => void;
}

type DropdownOutlinedComponent = FC<DropdownOutlinedProps> & {
  Button: typeof DropdownButton;
  Items: typeof DropdownItems;
};

const DropdownOutlined: DropdownOutlinedComponent = ({
  opened,
  disabled,
  hasActiveFilters,
  children,
  width,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, onClose, !!onClose && !!opened);

  return (
    <FilterWrapper ref={ref}>
      {Children.map(children, (child) =>
        isValidElement(child)
          ? cloneElement<any>(child, {
              opened,
              disabled,
              hasActiveFilters,
              wrapperRef: ref,
              width,
              ...((child.props as Clickable).onClick
                ? {
                    onClick: disabled
                      ? null
                      : (child.props as Clickable).onClick,
                  }
                : {}),
            })
          : child
      )}
    </FilterWrapper>
  );
};

DropdownOutlined.Button = DropdownButton;
DropdownOutlined.Items = DropdownItems;

export { Dropdown, DropdownInline, DropdownOutlined };
